// ContentAwareG1Gate — the redesigned Phase-1 review surface.
//
// Instead of the legacy abstract questions ("does the restated problem
// preserve every noun from T1?"), this panel:
//   1. Renders a side-by-side diff between the user's original problem
//      statement (T1) and the Contextualist's restate, with detected
//      nouns highlighted as chips.
//   2. Exposes the legacy gate decisions as per-item Keep / Edit /
//      Reject buttons — still writing the same backend gate payload.
//   3. Offers a prominent "Approve & run Phase 2" path once every
//      decision is made.
//
// Nothing here changes the /projects/{id}/runs/{n}/gates/1 contract:
// we submit the same GateDecisionRecord[] array, we just surface the
// questions on top of content the user can actually evaluate.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Edit3,
  FileText,
  Info,
  Loader2,
  PlayCircle,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import { stripFrontmatter } from '@/lib/markdown'
import type { PriorDecisionRecord } from '@/lib/api'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { toast } from '@/hooks/use-toast'
import type {
  GateDecisionRecord,
  GateDecisionStatus,
  Project,
} from '@/types'
import { detectNouns } from '@/components/intake/shapes'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'
import { AutoApproveAllButton } from '@/components/command-center/AutoApproveAllButton'
import { G1PriorDecisionsImport } from '@/components/command-center/G1PriorDecisionsImport'

export interface ContentAwareG1Props {
  project: Project
  runId: number
  /** Callback so the Timeline can re-focus after approval. */
  onApproved?: () => void
  /**
   * P1/G1 UX fix plan §A3 — invoked by the "Go to the Phase 1 briefing"
   * CTA rendered when the architect reaches this gate before Phase 1
   * has produced the charter. Keeps the surface actionable instead of
   * a dead-end "Waiting for Phase 1 to complete…" state.
   */
  onNavigateToBriefing?: () => void
}

export function ContentAwareG1Gate({
  project,
  runId,
  onApproved,
  onNavigateToBriefing,
}: ContentAwareG1Props) {
  const queryClient = useQueryClient()
  const draftKey = gateDraftKey(project.id, runId, 1)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)
  const [activeTab, setActiveTab] = useState<'content' | 'questions'>('content')
  const [charter, setCharter] = useState('')
  const [intentMap, setIntentMap] = useState('')

  // Share the artifact-index cache with the parent so we know whether
  // Phase-1 files (and the gate itself) are actually on disk before
  // hitting the file server / gate endpoint. Without this guard, a
  // gate1-focused mount before P1 has produced anything floods the
  // console with 404s.
  const artifactIndex = useQuery<{ files: { name: string }[] }>({
    queryKey: ['artifact-index', project.id, runId],
    queryFn: async () => {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(project.id)}/runs/${runId}/artifacts`,
      )
      if (!res.ok) throw new Error(`index fetch failed: ${res.status}`)
      return (await res.json()) as { files: { name: string }[] }
    },
    retry: false,
  })
  const charterPresent = (artifactIndex.data?.files ?? []).some(
    (f) => f.name === 'project-charter.md',
  )
  const intentMapPresent = (artifactIndex.data?.files ?? []).some(
    (f) => f.name === 'intent-map.md',
  )

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, 1],
    queryFn: () => api.getGate(project.id, runId, 1),
    enabled: charterPresent,
    retry: false,
  })

  // Grab charter + intent map so we can show the restate and goal hierarchy.
  useEffect(() => {
    if (!charterPresent && !intentMapPresent) return
    let cancelled = false
    async function load() {
      if (charterPresent) {
        try {
          const charterRes = await fetch(
            projectFileUrl(project.id, `runs/run-${String(runId).padStart(3, '0')}/phase1/project-charter.md`),
          )
          if (charterRes.ok && !cancelled) setCharter(await charterRes.text())
        } catch {
          /* ignore */
        }
      }
      if (intentMapPresent) {
        try {
          const intentRes = await fetch(
            projectFileUrl(project.id, `runs/run-${String(runId).padStart(3, '0')}/phase1/intent-map.md`),
          )
          if (intentRes.ok && !cancelled) setIntentMap(await intentRes.text())
        } catch {
          /* ignore */
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [project.id, runId, charterPresent, intentMapPresent])

  // Seed decisions from the freshly-fetched gate payload, reconciling
  // any persisted localStorage draft against the server's decision-id
  // set first. Without that reconciliation, a stale draft from a
  // deleted-and-recreated project (same auto-generated id) leaks
  // pre-selected answers into the freshly emitted gate. The helper
  // keeps the draft when its ids are a subset of the server's payload
  // (real in-flight edits the user authored on this project) and
  // discards-then-reseeds it otherwise.
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  // Stale-draft hygiene for the pre-Phase-1 case: when the artifact
  // index has loaded but neither the charter nor the gate exist, any
  // localStorage draft for this key was authored against a *previous*
  // run of this same project id (the user deleted the project on
  // disk and recreated it with the same auto-generated id, e.g.
  // ``proj_<YYYY_MM_DD>_<slug>`` collides on the same day). Without
  // this clear step the gate panel renders the prior project's
  // answers — the audit-flagged "Single-region active" / "Pooled
  // multi-tenancy" leak — even though there's no server-side gate
  // yet. We trigger only when (a) the index has finished loading,
  // (b) Phase 1 has not produced a charter, and (c) the gate hasn't
  // been emitted; the next Re-emit / Phase 1 run will reseed via
  // ``reconcileDraftWithGate`` above.
  useEffect(() => {
    if (!artifactIndex.isFetched) return
    if (charterPresent) return
    if (gate.data) return
    const existing = useGateDraftStore.getState().drafts[draftKey]
    if (!existing || existing.length === 0) return
    clearDecisionsDraft(draftKey)
  }, [
    artifactIndex.isFetched,
    charterPresent,
    gate.data,
    draftKey,
    clearDecisionsDraft,
  ])

  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, 1),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      gate.refetch()
      toast({ variant: 'success', title: 'G1 re-emitted' })
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Re-emit failed',
        description: (err as Error).message,
      }),
  })

  const submit = useMutation({
    mutationFn: () => api.submitGateDecisions(project.id, runId, 1, decisions),
    onSuccess: (g) => {
      gate.refetch()
      if (g.status === 'approved') {
        toast({
          variant: 'success',
          title: 'G1 approved',
          description: 'Ready to run Phase 2.',
        })
      }
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Could not submit decisions',
        description: (err as Error).message,
      }),
  })

  const runPhase2 = useMutation({
    mutationFn: async () => {
      await api.runPhase2(project.id, runId)
      return waitForPhaseCompletion(project.id, runId, 2)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifact-index', project.id, runId] })
      queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      onApproved?.()
      toast({
        variant: 'success',
        title: 'Phase 2 complete',
        description: 'Observer extracted constraints. Move on when ready.',
      })
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Phase 2 failed',
        description: (err as Error).message,
      }),
  })

  // Derived data -------------------------------------------------------- //
  const t1 = project.problem_statement ?? ''
  const restated = useMemo(() => extractCharterRestate(charter), [charter])
  const goalTree = useMemo(() => extractGoalTree(intentMap), [intentMap])
  const systemNoun = useMemo(() => extractSystemNoun(charter), [charter])
  const detectedNouns = useMemo(() => detectNouns(`${t1}\n${restated}`), [t1, restated])
  const topGoal = useMemo(() => extractTopGoal(charter, intentMap), [charter, intentMap])
  const clarifyingQuestions = useMemo(
    () => extractClarifyingQuestions(intentMap),
    [intentMap],
  )
  const clarifyingSuggestions = useMemo(
    () => extractSuggestionsByItem(intentMap, 'Clarifying Questions'),
    [intentMap],
  )
  const deferredUnknowns = useMemo(
    () => extractDeferredUnknowns(intentMap, charter),
    [intentMap, charter],
  )
  const deferredSuggestions = useMemo(
    () => extractSuggestionsByItem(intentMap, 'Deferred Unknowns', charter),
    [intentMap, charter],
  )

  const frozenNounSet = useMemo(
    () => new Set([systemNoun, ...detectedNouns].filter((x): x is string => Boolean(x))),
    [systemNoun, detectedNouns],
  )

  const allDecided =
    decisions.length > 0 && decisions.every((d) => d.status !== 'pending')
  // Server stamps the gate "modified" whenever any decision is in the
  // "edited" status (see services/hitl_store.py::apply_gate_decisions).
  // From the user's POV "modified" still means "I'm done with this gate
  // and want to run the next phase" — only "rejected" should block
  // forward progress. Treating "modified" as a green-light state lets
  // the Edit-then-Continue path actually move to Phase 2 instead of
  // looping forever on the Approve button.
  const gateCleared =
    gate.data?.status === 'approved' || gate.data?.status === 'modified'

  const updateDecision = (id: string, patch: Partial<GateDecisionRecord>) =>
    setDecisions(decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)))

  // Split decisions into content vs deferred-unknown vs question-style ones.
  const { contentDecisions, questionDecisions, deferredUnknownDecisions } =
    useMemo(() => {
      const cont: GateDecisionRecord[] = []
      const qns: GateDecisionRecord[] = []
      const dus: GateDecisionRecord[] = []
      for (const d of decisions) {
        if (d.id.startsWith('DU-')) {
          dus.push(d)
        } else if (/clarif/i.test(d.question) || /question/i.test(d.question)) {
          qns.push(d)
        } else {
          cont.push(d)
        }
      }
      return { contentDecisions: cont, questionDecisions: qns, deferredUnknownDecisions: dus }
    }, [decisions])

  // Resolve the actual content the user is being asked to evaluate for
  // each decision. Without this, the Keep/Edit/Reject buttons float over
  // an abstract question and Edit opens a blank textarea — the user has
  // no idea *what* they are editing. We map by question text (stable
  // across re-emits) and fall back to ID prefixes when the wording drifts.
  const targetContentFor = (d: GateDecisionRecord): TargetContent => {
    const q = (d.question ?? '').toLowerCase()
    if (
      /restated problem|preserve every noun|original problem statement/.test(q) ||
      d.id === 'G1-D1'
    ) {
      return {
        kind: 'paragraph',
        label: "Contextualist's restated problem",
        text: restated,
      }
    }
    if (/top-level goal|intent map.*align|aligned with your intent/.test(q) || d.id === 'G1-D2') {
      return {
        kind: 'paragraph',
        label: 'Top-level goal',
        text: topGoal,
      }
    }
    if (/clarifying question/.test(q) || d.id === 'G1-D3') {
      return {
        kind: 'list',
        label: 'Clarifying questions',
        items: clarifyingQuestions,
      }
    }
    if (/deferred unknown/.test(q) || d.id === 'G1-D4') {
      return {
        kind: 'list',
        label: 'Deferred unknowns',
        items: deferredUnknowns,
      }
    }
    return { kind: 'paragraph', label: '', text: '' }
  }

  const missing = gate.error instanceof ApiError && gate.error.status === 404

  // -------------------------------------------------------------------- //
  // P1/G1 UX fix plan §A3 — pre-Phase-1 guard. If the artifact index
  // has loaded and Phase 1 hasn't produced the charter, the review UI
  // below would be a dead end ("Waiting for Phase 1 to complete…" with
  // nothing actionable). Render an explicit empty state that routes
  // the architect back to the briefing where the Run button lives.
  // -------------------------------------------------------------------- //
  if (artifactIndex.isFetched && !charterPresent) {
    return (
      <div className="alm-fadein">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="alm-chip">Gate G1</span>
          <span className="alm-chip alm-chip-warn">
            <AlertCircle className="h-3 w-3" /> Waiting on Phase 1
          </span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-100">
          Nothing to review yet
        </h2>
        <p className="text-sm text-slate-400 mt-1 max-w-xl">
          Gate G1 reviews what the Contextualist produced in Phase 1 — the
          restated problem, goal hierarchy, and clarifying questions. Phase 1
          hasn't run yet, so there's nothing to review here.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="gate-1-goto-briefing"
            onClick={() => onNavigateToBriefing?.()}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500"
          >
            <ArrowLeft className="h-4 w-4" /> Go to the Phase 1 briefing
          </button>
          <span className="text-xs text-slate-500">
            Approve the briefing there, then click <strong>Run Phase 1</strong>.
            You'll be brought back here automatically when it finishes.
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="alm-fadein">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn">
              <AlertCircle className="h-3 w-3" /> Needs you
            </span>
            <span className="alm-chip">Gate G1</span>
            {decisions.length > 0 && (
              <span className="alm-chip alm-chip-info">
                {decisions.length} decision{decisions.length === 1 ? '' : 's'}
              </span>
            )}
            {gate.data?.status && (
              <span className="alm-chip">{gate.data.status}</span>
            )}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Review what the Contextualist produced
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Phase 1 is the cheapest place to catch scope drift. Every noun you keep here becomes
            frozen in downstream agents — a rename after Phase 2 costs a rewind.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => emit.mutate()}
            disabled={emit.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
          >
            {emit.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Re-emit
          </button>
          {gate.data?.artifact_path && (
            <a
              href={projectFileUrl(project.id, gate.data.artifact_path)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-400 hover:bg-white/5"
            >
              <FileText className="h-3 w-3" /> Raw artifact
            </a>
          )}
        </div>
      </div>

      {missing && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-5 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Click <strong>Re-emit</strong> above to build the G1 artifact (deterministic, no LLM
            spend).
          </p>
        </div>
      )}

      {/* Cross-run carry-forward (Architect Decisions Downstream Flow
          Plan §8 follow-up) — manual-import variant. Banner only
          renders when this run has at least one decision and a
          prior run with closed G1 exists. */}
      {decisions.length > 0 && (
        <G1PriorDecisionsImport
          projectId={project.id}
          runId={runId}
          onImportAll={(priorDecisions) =>
            setDecisions(applyPriorDecisions(decisions, priorDecisions))
          }
        />
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {/* Side-by-side diff */}
      {(t1 || restated) && (
        <div className="mb-6">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
            Side by side · original vs contextualist restate
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
                <FileText className="h-3 w-3" /> Your T1 problem statement
              </div>
              <div className="alm-diff">{renderWithNouns(t1, frozenNounSet)}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-indigo-400" /> Contextualist's Phase-1 restate
              </div>
              <div className="alm-diff">
                {restated ? renderWithNouns(restated, frozenNounSet) : (
                  <span className="text-slate-500">
                    Waiting for Phase 1 to complete…
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      {decisions.length > 0 && (
        <div className="flex items-center gap-1 border-b border-slate-700/60 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('content')}
            data-active={activeTab === 'content'}
            className="alm-tab"
          >
            Content decisions ({contentDecisions.length})
          </button>
          {questionDecisions.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveTab('questions')}
              data-active={activeTab === 'questions'}
              className="alm-tab"
            >
              Clarifying questions ({questionDecisions.length})
            </button>
          )}
          {goalTree.length > 0 && (
            <div className="ml-auto flex items-center gap-1.5 pr-1">
              <span className="text-[10.5px] text-slate-500">Goal hierarchy:</span>
              <span className="alm-chip">{goalTree.length} items</span>
            </div>
          )}
        </div>
      )}

      {/* Decision list */}
      {(activeTab === 'content' ? contentDecisions : questionDecisions).map((decision) => (
        <DecisionRow
          key={decision.id}
          gateNumber={1}
          decision={decision}
          target={targetContentFor(decision)}
          clarifyingQuestions={clarifyingQuestions}
          // LLM-authored suggestions, keyed by question text. These take
          // priority over the regex-based mining inside the editor.
          authoredSuggestions={
            targetContentFor(decision).label === 'Clarifying questions'
              ? clarifyingSuggestions
              : targetContentFor(decision).label === 'Deferred unknowns'
              ? deferredSuggestions
              : new Map()
          }
          onChange={(patch) => updateDecision(decision.id, patch)}
        />
      ))}

      {/* Goal hierarchy readout (collapsed, read-only preview) */}
      {goalTree.length > 0 && activeTab === 'content' && (
        <div className="alm-card mt-3">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
            Goal hierarchy (auto-approved unless you flag above)
          </div>
          <ul className="text-sm text-slate-300 space-y-1">
            {goalTree.slice(0, 8).map((g, i) => (
              <li key={`${g.text}-${i}`} style={{ paddingLeft: `${g.depth * 14}px` }}>
                <span className="text-indigo-400 mr-1">{g.depth === 0 ? '●' : '◦'}</span>
                {g.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Deferred Unknowns Q&A section — each unknown is presented as a
          fillable field so the architect can answer at G1 before Phase 2.
          Answers are stored in `decision.answer` and persisted via the
          standard gate decisions POST. */}
      {deferredUnknownDecisions.length > 0 && (
        <div className="alm-card mt-4 border-indigo-500/30">
          <div className="flex items-start gap-2 mb-3">
            <Info className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-100">
                Deferred Unknowns ({deferredUnknownDecisions.length})
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                The charter flagged these as unknowns. Answer each one now —
                your responses are consumed by downstream agents (Observer, BA,
                Safety/Compliance) in later phases. Leave blank to keep it
                deferred.
              </p>
            </div>
            <span className="alm-chip alm-chip-info ml-auto shrink-0">
              {deferredUnknownDecisions.filter((d) => (d.answer ?? '').trim().length > 0).length}/
              {deferredUnknownDecisions.length} answered
            </span>
          </div>
          <div className="space-y-3">
            {deferredUnknownDecisions.map((d) => {
              const cleanedQuestion = cleanDeferredUnknownQuestion(d.question)
              // Priority 1: structured option cards from the Virtual
              // Architect (panel.options on the decision payload). These
              // ship rich title/summary/pros/cons and represent the
              // best-quality choice surface the BA can use without a
              // domain expert. Rendered as cards (not chips) so the
              // summary text is visible at a glance.
              const panelOptions = extractArchitectPanelOptions(d.payload)

              // Priority 2: LLM-authored "Suggested answers:" from the
              // intent-map / charter. Already domain-aware and concise
              // enough to render as chips.
              const authored =
                lookupSuggestions(deferredSuggestions, cleanedQuestion) ?? []

              // Priority 3: heuristic mining off the question text +
              // a matched clarifying question. Fallback only.
              const heuristicReference = findMatchingQuestion(
                cleanedQuestion,
                clarifyingQuestions,
              )
              const heuristicChips =
                authored.length > 0
                  ? authored
                  : suggestAnswers(cleanedQuestion, heuristicReference)
              const currentAnswer = (d.answer ?? '').trim()

              // Decide which chip set drives the picker.
              //   * Architect panel present → use panel.options as
              //     rich cards. We still show heuristic chips as a
              //     secondary "quick answers" row so the architect
              //     can pick the cheap "Defer" option without
              //     digging into a card.
              //   * Otherwise → render the chip set we computed
              //     (authored or heuristic).
              const hasPanel = panelOptions.length > 0
              const chips = hasPanel ? [] : heuristicChips
              return (
                <div key={d.id} className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-mono text-indigo-400 mt-0.5 shrink-0">
                      {d.id}
                    </span>
                    <label
                      htmlFor={`du-answer-${d.id}`}
                      className="text-xs font-medium text-slate-200 leading-snug"
                    >
                      {cleanedQuestion}
                    </label>
                  </div>

                  {hasPanel && (
                    <div className="pl-6 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {panelOptions.map((opt) => {
                          const selected = currentAnswer === opt.title
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() =>
                                updateDecision(d.id, {
                                  answer: selected ? '' : opt.title,
                                  status:
                                    d.status === 'pending' ? 'edited' : d.status,
                                })
                              }
                              className={[
                                'text-left rounded-md border px-2.5 py-2 transition',
                                selected
                                  ? 'border-indigo-500/70 bg-indigo-950/30 ring-1 ring-indigo-500/50'
                                  : 'border-slate-700/60 bg-slate-900/40 hover:border-slate-600 hover:bg-slate-900/60',
                              ].join(' ')}
                              title={opt.summary}
                            >
                              <div className="flex items-center gap-1.5 mb-0.5">
                                {selected && <Check className="h-3 w-3 text-indigo-300" />}
                                <span
                                  className={[
                                    'text-[11.5px] font-semibold leading-tight',
                                    selected ? 'text-indigo-200' : 'text-slate-200',
                                  ].join(' ')}
                                >
                                  {opt.title}
                                </span>
                                {opt.recommended && (
                                  <span className="alm-chip alm-chip-info text-[9px] px-1 py-0">
                                    recommended
                                  </span>
                                )}
                              </div>
                              <div className="text-[10.5px] text-slate-400 leading-snug line-clamp-2">
                                {opt.summary}
                              </div>
                            </button>
                          )
                        })}
                      </div>
                      {/* Always offer a "Defer" escape hatch so the BA
                          can keep the unknown open without inventing a
                          fake answer. */}
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            const deferAnswer = 'Defer — answer in Phase 2'
                            const selected = currentAnswer === deferAnswer
                            updateDecision(d.id, {
                              answer: selected ? '' : deferAnswer,
                              status:
                                d.status === 'pending' ? 'edited' : d.status,
                            })
                          }}
                          data-selected={
                            currentAnswer === 'Defer — answer in Phase 2'
                              ? 'true'
                              : undefined
                          }
                          className="alm-decision text-[11px]"
                        >
                          Defer — answer in Phase 2
                        </button>
                      </div>
                    </div>
                  )}

                  {chips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pl-6">
                      {chips.map((s) => {
                        const selected = currentAnswer === s
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() =>
                              updateDecision(d.id, {
                                answer: selected ? '' : s,
                                status:
                                  d.status === 'pending' ? 'edited' : d.status,
                              })
                            }
                            data-selected={selected ? 'true' : undefined}
                            className="alm-decision text-[11px]"
                          >
                            {selected ? <Check className="h-3 w-3" /> : null}
                            {s}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  <textarea
                    id={`du-answer-${d.id}`}
                    value={d.answer ?? ''}
                    onChange={(e) =>
                      updateDecision(d.id, {
                        answer: e.target.value,
                        status: d.status === 'pending' ? 'edited' : d.status,
                      })
                    }
                    rows={2}
                    placeholder={
                      hasPanel || chips.length > 0
                        ? 'Or type a different answer…'
                        : 'Type your answer or leave blank to defer…'
                    }
                    className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Why-this-matters explainer */}
      <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mt-5 mb-5 flex gap-2 text-xs text-slate-400">
        <Info className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <strong className="text-slate-200">Why this gate exists:</strong> every downstream
          artifact references these nouns by exact string. The Critic later flags any phrase
          drift as a requirement defect — so catching renames here costs nothing, whereas fixing
          them at G4 costs a rerun of Phase 3 (5–10 min and ~$0.90 in LLM costs).
        </div>
      </div>

      {/* Primary actions */}
      <div className="flex flex-wrap items-center gap-2">
        {gateCleared ? (
          <button
            type="button"
            onClick={() => runPhase2.mutate()}
            disabled={runPhase2.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 hover:from-indigo-400 hover:to-indigo-500"
          >
            {runPhase2.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Running Phase 2…
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4" /> Run Phase 2
              </>
            )}
          </button>
        ) : (
          <>
            {/* Plan §P1.12 — match G4's "trust the agent" 1-click bulk
                approve. We exclude Deferred Unknown rows (DU-) from the
                skip predicate so the architect's blank answers don't
                end up rubber-stamped. */}
            <AutoApproveAllButton
              decisions={decisions}
              setDecisions={setDecisions}
              gateName="G1"
              disabled={submit.isPending}
              skip={(d) => d.id.startsWith('DU-')}
            />
            <button
              type="button"
              data-testid="gate-1-submit"
              onClick={() => submit.mutate()}
              disabled={!allDecided || submit.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
              title={
                !allDecided ? 'Choose Approve / Edit / Reject for every decision first.' : undefined
              }
            >
              {submit.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" /> Approve & continue
                </>
              )}
            </button>
          </>
        )}
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5"
          onClick={() => emit.mutate()}
        >
          <ArrowLeft className="h-4 w-4" /> Rewind to intake
        </button>
        {!allDecided && !gateCleared && decisions.length > 0 && (
          <span className="text-xs text-slate-500">
            Decide every item first.
          </span>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------- //
// Target content — the actual artifact slice each decision applies to.
// Surfaced inline so the user can see *what* they are keeping/editing/
// rejecting, and so Edit pre-fills with that content as a starting draft.
// ---------------------------------------------------------------------- //
type TargetContent =
  | { kind: 'paragraph'; label: string; text: string }
  | { kind: 'list'; label: string; items: string[] }

function targetToEditableString(t: TargetContent): string {
  if (t.kind === 'paragraph') return t.text ?? ''
  return (t.items ?? []).map((line, i) => `${i + 1}. ${line}`).join('\n')
}

// ---------------------------------------------------------------------- //
// DecisionRow — Keep / Edit / Reject triad with inline reason fields.
// ---------------------------------------------------------------------- //
function DecisionRow({
  gateNumber,
  decision,
  target,
  clarifyingQuestions,
  authoredSuggestions,
  onChange,
}: {
  /**
   * Gate identifier (1, 2, 3, etc.). Drives the per-gate
   * ``data-testid`` hooks (T2 — plan §5.5 / check-testids.mjs) so a
   * Playwright spec can target ``gate-1-row`` / ``gate-1-approve``
   * etc. independently of the gate panel's prose copy.
   */
  gateNumber: number
  decision: GateDecisionRecord
  target: TargetContent
  /**
   * Phase-1 clarifying questions; used only by the Deferred-Unknowns
   * editor to seed answer suggestions (the clarifying-question text
   * usually lists the candidate options inline, e.g. "multi-tenant
   * SaaS, single-tenant SaaS, on-prem appliance").
   */
  clarifyingQuestions: string[]
  /**
   * LLM-authored suggested answers extracted from the intent-map's
   * `Suggested answers:` sub-lists. Keyed by question/unknown text so
   * the picker can show the Contextualist's actual recommendations
   * instead of regex-mined fragments. Empty when the prompt didn't
   * supply any (older runs / models that ignored the rule).
   */
  authoredSuggestions: Map<string, string[]>
  onChange: (patch: Partial<GateDecisionRecord>) => void
}) {
  // Map the canonical triad action to the testid suffix used by
  // ``check-testids.mjs``. The plan documents the *user-facing* verb
  // (``approve`` / ``modify`` / ``reject``) — the in-memory status
  // string differs (``approved`` / ``edited`` / ``rejected``) so the
  // spec language stays human-readable.
  const triad: Array<{
    s: GateDecisionStatus
    label: string
    testSuffix: 'approve' | 'modify' | 'reject'
    icon: React.ComponentType<{ className?: string }>
  }> = [
    { s: 'approved', label: 'Approve', testSuffix: 'approve', icon: Check },
    { s: 'edited', label: 'Edit', testSuffix: 'modify', icon: Edit3 },
    { s: 'rejected', label: 'Reject', testSuffix: 'reject', icon: X },
  ]
  const hasTarget =
    (target.kind === 'paragraph' && (target.text ?? '').trim().length > 0) ||
    (target.kind === 'list' && (target.items ?? []).length > 0)

  // Click "Edit" → swap to edited status AND seed the textarea with the
  // current artifact content. This is the core fix: previously Edit
  // opened a blank textarea, so the user had nothing to edit.
  const handleStatusChange = (next: GateDecisionStatus) => {
    if (next === 'edited' && (decision.answer ?? '').trim().length === 0 && hasTarget) {
      onChange({ status: next, answer: targetToEditableString(target) })
      return
    }
    onChange({ status: next })
  }

  return (
    // E2E hooks (T2 — plan §5.5 / check-testids.mjs). One row per
    // decision; the gate-{n}-* family lets specs target rows and
    // their action triad without scraping the visible prose.
    <div className="alm-card mb-2.5" data-testid={`gate-${gateNumber}-row`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[10.5px] text-slate-500">{decision.id}</div>
          <div className="text-sm text-slate-100 mt-0.5">{decision.question}</div>
        </div>
        <div className="flex gap-1.5 shrink-0 flex-wrap">
          {triad.map((t) => (
            <button
              type="button"
              key={t.s}
              data-testid={`gate-${gateNumber}-${t.testSuffix}`}
              onClick={() => handleStatusChange(t.s)}
              className="alm-decision"
              data-selected={decision.status === t.s ? decision.status : undefined}
            >
              <t.icon className="h-3 w-3" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Target content preview — what the user is actually deciding on. */}
      {hasTarget && (
        <div className="rounded-md border border-slate-700/50 bg-slate-950/40 p-3 mb-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
            {target.label}
          </div>
          {target.kind === 'paragraph' ? (
            <p className="text-[13px] leading-relaxed text-slate-200 whitespace-pre-wrap">
              {target.text}
            </p>
          ) : (
            <ol className="text-[13px] leading-relaxed text-slate-200 space-y-1 list-decimal pl-5">
              {target.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ol>
          )}
        </div>
      )}

      {decision.status === 'edited' && target.kind === 'list' && target.label === 'Deferred unknowns' ? (
        <PerItemAnswerEditor
          mode="unknowns"
          items={target.items}
          // For unknowns we mine candidate answers off the *clarifying
          // question* that best matches each unknown.
          referenceQuestions={clarifyingQuestions}
          authoredSuggestions={authoredSuggestions}
          initialAnswer={decision.answer ?? ''}
          onAnswerChange={(serialized) => onChange({ answer: serialized })}
        />
      ) : decision.status === 'edited' && target.kind === 'list' && target.label === 'Clarifying questions' ? (
        <PerItemAnswerEditor
          mode="questions"
          items={target.items}
          // For clarifying questions we mine options out of the question
          // text itself — references aren't needed.
          referenceQuestions={[]}
          authoredSuggestions={authoredSuggestions}
          initialAnswer={decision.answer ?? ''}
          onAnswerChange={(serialized) => onChange({ answer: serialized })}
        />
      ) : decision.status === 'edited' ? (
        <>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Your edit (will replace the {target.kind === 'list' ? 'list' : 'text'} above)
          </div>
          <textarea
            value={decision.answer ?? ''}
            onChange={(e) => onChange({ answer: e.target.value })}
            rows={Math.max(3, Math.min(12, (decision.answer ?? '').split('\n').length + 1))}
            placeholder={
              hasTarget
                ? 'Edit the content above, or describe the change…'
                : 'Describe the edit the Contextualist should apply…'
            }
            className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 font-mono"
          />
          {hasTarget && (
            <button
              type="button"
              onClick={() => onChange({ answer: targetToEditableString(target) })}
              className="text-[10.5px] text-slate-500 hover:text-slate-300 mt-1"
            >
              ↺ Reset to original
            </button>
          )}
        </>
      ) : null}
      {decision.status === 'rejected' && (
        <textarea
          data-testid={`gate-${gateNumber}-rationale`}
          value={decision.rejection_reason ?? ''}
          onChange={(e) => onChange({ rejection_reason: e.target.value })}
          rows={2}
          placeholder="Why are you rejecting this?"
          className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// Charter / intent-map parsers (lightweight — only need a usable preview)
// ``stripFrontmatter`` comes from the shared @/lib/markdown helper.
// ---------------------------------------------------------------------- //
function extractSystemNoun(charter: string): string | null {
  const body = stripFrontmatter(charter)
  // Heuristic: first **Bold** phrase in the executive summary.
  const match = body.match(/\*\*([A-Z][A-Za-z0-9 _-]{2,60})\*\*/)
  return match ? match[1].trim() : null
}

function extractCharterRestate(charter: string): string {
  const body = stripFrontmatter(charter)
  // Prefer the first paragraph under the Executive Summary heading.
  // Allow numbered headings like "# 1. Executive Summary".
  const exec = body.match(
    /#+\s*(?:\d+[.)]\s*)?Executive\s+Summary[^\n]*\n+([\s\S]*?)(?:\n#+|\n---|$)/i,
  )
  if (exec) return exec[1].trim().replace(/\s+/g, ' ').slice(0, 1200)
  // Fallback to first non-heading paragraph.
  const para = body
    .split(/\n\n/)
    .map((p) => p.trim())
    .filter((p) => p && !p.startsWith('#'))
  return (para[0] ?? '').slice(0, 1200)
}

function extractGoalTree(intentMap: string): Array<{ depth: number; text: string }> {
  const body = stripFrontmatter(intentMap)
  // Allow numbered headings like "# 2. Goal Hierarchy".
  const section = body.match(
    /#+\s*(?:\d+[.)]\s*)?Goal\s+Hierarchy[^\n]*\n+([\s\S]*?)(?:\n#+|\n---|$)/i,
  )
  const src = section ? section[1] : body
  const rows: Array<{ depth: number; text: string }> = []
  for (const line of src.split('\n')) {
    const m = line.match(/^(\s*)[-*•]\s+(.*)$/)
    if (m) {
      const depth = Math.floor(m[1].length / 2)
      const text = m[2].replace(/`/g, '').trim()
      if (text) rows.push({ depth, text })
    }
    if (rows.length >= 12) break
  }
  return rows
}

// Renders text with frozen-noun chips inlined.
function renderWithNouns(text: string, nouns: Set<string>) {
  if (!text) return null
  if (nouns.size === 0) return text
  const tokens: Array<{ kind: 'text' | 'noun'; value: string }> = [{ kind: 'text', value: text }]
  for (const noun of nouns) {
    if (!noun || noun.length < 2) continue
    const re = new RegExp(`\\b${escapeRegex(noun)}\\b`)
    const next: typeof tokens = []
    for (const t of tokens) {
      if (t.kind !== 'text') { next.push(t); continue }
      let rest = t.value
      while (rest.length) {
        const m = rest.match(re)
        if (!m || m.index === undefined) { next.push({ kind: 'text', value: rest }); break }
        if (m.index > 0) next.push({ kind: 'text', value: rest.slice(0, m.index) })
        next.push({ kind: 'noun', value: m[0] })
        rest = rest.slice(m.index + m[0].length)
      }
    }
    tokens.splice(0, tokens.length, ...next)
  }
  return tokens.map((t, i) =>
    t.kind === 'noun' ? (
      <span key={i} className="alm-noun">
        {t.value}
      </span>
    ) : (
      <span key={i}>{t.value}</span>
    ),
  )
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Pull the Top Goal off either the charter or intent-map. The Phase-1
// Contextualist consistently writes "**Top Goal:** …" on a single line
// inside the Goal Hierarchy section.
function extractTopGoal(charter: string, intentMap: string): string {
  for (const md of [charter, intentMap]) {
    const body = stripFrontmatter(md)
    const m = body.match(/\*\*Top\s*Goal:?\*\*\s*([^\n]+)/i)
    if (m) return m[1].replace(/`/g, '').trim()
    // Fallback: a bullet line starting with "Top Goal:".
    const bullet = body.match(/^[\s>*-]*Top\s*Goal:?\s*([^\n]+)/im)
    if (bullet) return bullet[1].replace(/`/g, '').trim()
  }
  return ''
}

// Pull the numbered clarifying questions out of the intent-map's
// "Clarifying Questions" section. Allow numbered headings like
// "# 4. Clarifying Questions".
//
// We treat each top-level numbered/bulleted line as ONE question and
// stop accumulating into that question as soon as we hit a nested
// "Suggested answers:" header — those nested chips are extracted
// separately by `extractSuggestionsByItem`. This way the question text
// shown in the UI stays clean ("KYC/AML provider and which national
// databases are authoritative") instead of including the whole answer
// list as part of the question.
function extractClarifyingQuestions(intentMap: string): string[] {
  const body = stripFrontmatter(intentMap)
  const section = body.match(
    /#+\s*(?:\d+[.)]\s*)?Clarifying\s+Questions[^\n]*\n+([\s\S]*?)(?:\n#+|\n---|$)/i,
  )
  if (!section) return []
  return parseTopLevelItems(section[1])
}

// Pull the deferred-unknowns checklist. Prefer the intent-map's
// "Deferred Unknowns" section, then fall back to the charter.
// Allow numbered headings like "# 5. Deferred Unknowns".
//
// Like clarifying questions above, we stop on a nested "Suggested
// answers:" so the unknown text alone (without the chip list) ends up
// in the picker.
function extractDeferredUnknowns(intentMap: string, charter: string): string[] {
  for (const md of [intentMap, charter]) {
    const body = stripFrontmatter(md)
    const section = body.match(
      /#+\s*(?:\d+[.)]\s*)?Deferred\s+Unknowns[^\n]*\n+([\s\S]*?)(?:\n#+|\n---|$)/i,
    )
    if (!section) continue
    const items = parseTopLevelItems(section[1])
    if (items.length) return items
  }
  return []
}

// ---------------------------------------------------------------------- //
// Top-level list parser shared by clarifying-questions + deferred-unknowns.
//
// The Phase-1 prompt now requires every question / unknown to be
// followed by a `Suggested answers:` sub-list. We need to:
//
//   * Treat each top-level "1. …" / "- [ ] …" line as the START of one
//     item. Subsequent indented lines (sub-bullets, multi-line wrapping
//     of the same question) belong to that item.
//   * Stop accumulating prose into the item title once we hit
//     `Suggested answers:`. The chips themselves are pulled out by
//     `extractSuggestionsByItem` below.
//   * Strip markdown bold / backticks / trailing whitespace from the
//     final string so the picker shows a clean sentence.
// ---------------------------------------------------------------------- //
function parseTopLevelItems(src: string): string[] {
  const lines = src.split('\n')
  const items: string[] = []
  let currentLines: string[] | null = null
  let inSuggestions = false

  const flush = () => {
    if (currentLines && currentLines.length > 0) {
      const joined = currentLines
        .join(' ')
        .replace(/\s+/g, ' ')
        .replace(/`/g, '')
        .trim()
      if (joined) items.push(joined)
    }
    currentLines = null
    inSuggestions = false
  }

  // A "top-level" bullet starts at column 0 with no leading indentation.
  // The Contextualist's Markdown convention indents nested chip lists
  // by 2+ spaces — we deliberately match column 0 ONLY so a chip line
  // like "  - Jumio (KYC + …)" is never mistaken for a new question.
  const TOP_RE = /^(?:[-*•]\s*\[[ x]\]\s*|[-*•]\s+|\d+[.)]\s+)(.*)$/
  // A "definitely indented" line (anything starting with a space) is
  // a continuation of the previous top-level item — either wrapped
  // question text, the "Suggested answers:" header, or a chip bullet.
  const INDENTED_RE = /^\s+\S/

  for (const raw of lines) {
    const stripped = raw.replace(/\r$/, '')
    if (!stripped.trim()) {
      // Blank line — keep the current item open; the LLM sometimes
      // breaks mid-sentence when it wraps. We just skip the blank.
      continue
    }
    // Heuristic: hitting a "Suggested answers:" header (case-insensitive)
    // closes the question text; chips are handled by the suggestions
    // parser, not by us. Check this BEFORE the top-level regex so an
    // indented "  - Jumio …" chip doesn't escape the suggestions block.
    if (/^\s*suggested\s+answers?\s*:/i.test(stripped)) {
      inSuggestions = true
      continue
    }
    if (inSuggestions && INDENTED_RE.test(stripped)) {
      // Still inside the chip list — skip.
      continue
    }
    const top = stripped.match(TOP_RE)
    if (top) {
      flush()
      currentLines = [top[1].trim()]
      continue
    }
    if (inSuggestions) continue
    // Otherwise this is a continuation line of the current item — append.
    if (currentLines !== null) {
      currentLines.push(stripped.trim())
    }
  }
  flush()
  return items
}

// Extract the per-item `Suggested answers:` chip lists keyed by the
// item text. We walk the section the same way `parseTopLevelItems`
// does, then for each top-level item we collect the indented bullets
// that follow `Suggested answers:` and stop at the next top-level
// item (or end of section).
//
// The result is a Map<itemText, string[]>. Lookups should be done via
// `lookupSuggestions`, which normalises whitespace + bold to make
// matching robust against minor formatting drift.
function extractSuggestionsByItem(
  intentMap: string,
  sectionName: 'Clarifying Questions' | 'Deferred Unknowns',
  fallback: string = '',
): Map<string, string[]> {
  // For Deferred Unknowns we MERGE chips from both sources (intent-map
  // §5 + charter §7). The two artifacts often list different unknowns
  // (the intent-map trims to the most actionable; the charter keeps
  // the full set), so picking only one drops chips for half the
  // unknowns the gate actually surfaces. Intent-map wins on a key
  // collision because its wording is the "canonical" Q&A surface.
  const result = new Map<string, string[]>()
  const sources = sectionName === 'Deferred Unknowns' ? [intentMap, fallback] : [intentMap]
  // Walk in REVERSE so the earlier (preferred) source overwrites the
  // later one on key collision — Map.set() is last-write-wins, so we
  // load the fallback first and intent-map second.
  for (const md of [...sources].reverse()) {
    if (!md) continue
    const body = stripFrontmatter(md)
    const sectionRe = new RegExp(
      `#+\\s*(?:\\d+[.)]\\s*)?${sectionName.replace(/\s+/g, '\\s+')}[^\\n]*\\n+([\\s\\S]*?)(?:\\n#+|\\n---|$)`,
      'i',
    )
    const match = body.match(sectionRe)
    if (!match) continue
    // Column-0 ONLY for top-level items — chip rows are indented and
    // must never look like the start of a new question.
    const TOP_RE = /^(?:[-*•]\s*\[[ x]\]\s*|[-*•]\s+|\d+[.)]\s+)(.*)$/
    const INDENTED_RE = /^\s+\S/
    const lines = match[1].split('\n')

    let currentKeyLines: string[] | null = null
    let inSuggestions = false
    let chips: string[] = []

    const flush = () => {
      if (currentKeyLines && chips.length > 0) {
        const key = currentKeyLines
          .join(' ')
          .replace(/\s+/g, ' ')
          .replace(/`/g, '')
          .trim()
        if (key) result.set(key, [...chips])
      }
      currentKeyLines = null
      inSuggestions = false
      chips = []
    }

    for (const raw of lines) {
      const stripped = raw.replace(/\r$/, '')
      if (!stripped.trim()) continue
      // Suggested-answers header check FIRST so an indented chip is
      // never matched as a top-level item.
      if (/^\s*suggested\s+answers?\s*:/i.test(stripped)) {
        inSuggestions = true
        continue
      }
      if (inSuggestions && INDENTED_RE.test(stripped)) {
        // We're inside the chip list — collect any bulleted line.
        const chipMatch = stripped.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)$/)
        if (chipMatch) {
          const chip = chipMatch[1].replace(/`/g, '').trim()
          if (chip) chips.push(chip)
        }
        continue
      }
      const top = stripped.match(TOP_RE)
      if (top) {
        flush()
        currentKeyLines = [top[1].trim()]
        continue
      }
      if (inSuggestions) continue
      // Continuation of the question/unknown text itself.
      if (currentKeyLines !== null) {
        currentKeyLines.push(stripped.trim())
      }
    }
    flush()
    // Don't break early — the next source (intent-map after charter)
    // overwrites collisions, so its keys still take precedence.
  }
  return result
}

// Normalised lookup — strip bold + collapse whitespace on both sides
// so minor formatting drift (e.g. `**EHR target.** Should …` vs
// `EHR target. Should …`) doesn't lose the match.
function lookupSuggestions(
  map: Map<string, string[]>,
  key: string,
): string[] | null {
  // Strip the trailing " — ask: <stakeholder>" annotation off both
  // sides of the comparison. The charter writes unknowns with the
  // stakeholder appended (`FedRAMP impact level (...) — ask:
  // compliance lead`); the gate question strips it. Without this
  // normalisation the lookup always misses for charter-keyed chips.
  const stripAsk = (s: string) =>
    s.replace(/\s*[—-]\s*ask:\s*[^\n]*$/i, '').trim()
  const norm = (s: string) =>
    stripAsk(s)
      .replace(/\*\*/g, '')
      .replace(/`/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\.\s*$/, '')
      .trim()
      .toLowerCase()
  const target = norm(key)
  for (const [k, v] of map.entries()) {
    if (norm(k) === target) return v
  }
  // Fallback: prefix match (LLM occasionally adds a trailing
  // parenthetical to the question text on re-emit but keeps the
  // suggestions keyed under the prior wording).
  for (const [k, v] of map.entries()) {
    const nk = norm(k)
    if (nk.length > 20 && (target.startsWith(nk) || nk.startsWith(target))) {
      return v
    }
  }
  // Last-ditch: token-overlap fuzzy match. Two keys with ≥60% shared
  // ≥4-char tokens (case-insensitive) are treated as the same item.
  // Catches LLM rewording on re-emit ("FedRAMP impact level" vs
  // "Target FedRAMP impact level for v1") that prefix matching misses.
  const tokensOf = (s: string) =>
    new Set(
      s
        .toLowerCase()
        .replace(/[^a-z0-9\s/_-]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length >= 4),
    )
  const tTok = tokensOf(target)
  if (tTok.size >= 2) {
    let best: { v: string[]; ratio: number } | null = null
    for (const [k, v] of map.entries()) {
      const kTok = tokensOf(norm(k))
      if (kTok.size === 0) continue
      let inter = 0
      for (const w of tTok) if (kTok.has(w)) inter++
      const ratio = inter / Math.max(tTok.size, kTok.size)
      if (!best || ratio > best.ratio) best = { v, ratio }
    }
    if (best && best.ratio >= 0.6) return best.v
  }
  return null
}

// ---------------------------------------------------------------------- //
// PerItemAnswerEditor — generic per-item answer picker.
//
// Used for both Deferred Unknowns (G1-D4) and Clarifying Questions
// (G1-D3). For each item we render one card with 3–6 suggested answers
// as clickable chips plus a free-text input.
//
// Suggestions are derived deterministically (no LLM call):
//   * `unknowns` mode mines candidates off the *clarifying question*
//     that most closely covers the unknown, then off the unknown text
//     itself — clarifying questions almost always enumerate options
//     inline ("multi-tenant SaaS, single-tenant SaaS, on-prem").
//   * `questions` mode mines straight off the question text. Phase-1
//     clarifying questions follow the same convention, so this works
//     even though there's no separate reference list to consult.
//
// The editor serialises into a single string written back to
// `decision.answer` so the existing /gates/{n} payload contract is
// unchanged — each item becomes a numbered "<item> — <answer>" block.
// Re-opening Edit re-parses the saved string so prior picks persist.
// ---------------------------------------------------------------------- //
function PerItemAnswerEditor({
  mode,
  items,
  referenceQuestions,
  authoredSuggestions,
  initialAnswer,
  onAnswerChange,
}: {
  mode: 'unknowns' | 'questions'
  items: string[]
  referenceQuestions: string[]
  /**
   * LLM-authored answer chips, keyed by item text. When a row's text
   * has authored suggestions we render them verbatim instead of
   * regex-mining the question — see prompts/contextualist/v1.md.
   */
  authoredSuggestions: Map<string, string[]>
  initialAnswer: string
  onAnswerChange: (serialized: string) => void
}) {
  // Per-item free-text answer state (chip selection just writes into
  // this same map, so chip-then-tweak works without losing input).
  const [answers, setAnswers] = useState<string[]>(() =>
    parseSerializedAnswers(initialAnswer, items.length),
  )

  // If the items list size changes (e.g. Re-emit produced a different
  // number of clarifying questions), rehydrate from the latest answer.
  useEffect(() => {
    setAnswers(parseSerializedAnswers(initialAnswer, items.length))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  // IMPORTANT: do NOT call the parent's onAnswerChange from inside a
  // setAnswers updater. React updater functions must be pure; setState
  // calls scheduled there fire while the parent is rendering, which
  // produces "Cannot update a component (`ContentAwareG1Gate`) while
  // rendering a different component (`PerItemAnswerEditor`)". Compute
  // the next array first, then update local state and notify the parent
  // synchronously after — both events end up in the same React commit
  // so there is no double render or torn state.
  const setAnswerAt = (i: number, val: string) => {
    const next = answers.slice()
    next[i] = val
    setAnswers(next)
    onAnswerChange(serializeAnswers(items, next))
  }

  // Resolve suggestions for each item.
  //
  // Priority order:
  //   1. LLM-authored chips for *this* item (the Contextualist's
  //      "Suggested answers:" sub-list). These are domain-aware and
  //      almost always better than what we can mine.
  //   2. Heuristic mining off a matched reference question (unknowns
  //      mode only).
  //   3. Heuristic mining off the item text itself.
  // We append "Defer — answer in Phase 2" once at the end so the user
  // always has an escape hatch even when the LLM forgot to include one.
  const suggestionsByIndex = useMemo(
    () =>
      items.map((item) => {
        const authored = lookupSuggestions(authoredSuggestions, item) ?? []
        if (authored.length > 0) {
          const out = authored.slice(0, 6)
          if (!out.some((s) => /^defer\b/i.test(s))) {
            out.push('Defer — answer in Phase 2')
          }
          return out
        }
        const reference =
          mode === 'unknowns'
            ? findMatchingQuestion(item, referenceQuestions)
            : null
        return suggestAnswers(item, reference)
      }),
    [items, referenceQuestions, mode, authoredSuggestions],
  )

  const idPrefix = mode === 'unknowns' ? 'DU' : 'CQ'
  const blurb =
    mode === 'unknowns'
      ? 'Pick a suggested option per item or type your own. Your answers replace the deferred-unknowns list and feed downstream agents.'
      : 'Answer each clarifying question now so the Contextualist can fold your responses into Phase 2 without re-asking. Pick a suggested option or type your own.'

  return (
    <div className="rounded-md border border-indigo-500/30 bg-slate-950/50 p-3">
      <div className="flex items-start gap-2 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-indigo-400 shrink-0 mt-0.5" />
        <div className="text-[11px] text-slate-400 leading-snug">
          <strong className="text-slate-200">
            {mode === 'unknowns' ? 'Answer the unknowns.' : 'Answer the questions.'}
          </strong>{' '}
          {blurb}
        </div>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="rounded-md border border-slate-700/60 bg-slate-950/40 p-2.5">
            <div className="flex items-start gap-2 mb-1.5">
              <span className="text-[10px] font-mono text-indigo-400 mt-0.5 shrink-0">
                {idPrefix}-{i + 1}
              </span>
              <div className="text-[12.5px] text-slate-100 leading-snug">
                {stripBoldMarkdown(item)}
              </div>
            </div>
            {suggestionsByIndex[i].length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {suggestionsByIndex[i].map((s) => {
                  const selected = (answers[i] ?? '').trim() === s
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setAnswerAt(i, selected ? '' : s)}
                      data-selected={selected ? 'true' : undefined}
                      className="alm-decision text-[11px]"
                    >
                      {selected ? <Check className="h-3 w-3" /> : null}
                      {s}
                    </button>
                  )
                })}
              </div>
            )}
            <input
              type="text"
              value={answers[i] ?? ''}
              onChange={(e) => setAnswerAt(i, e.target.value)}
              placeholder={
                suggestionsByIndex[i].length > 0
                  ? 'Or type a different answer…'
                  : 'Type your answer…'
              }
              className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-2.5 py-1.5 text-[12px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// Strip the leading "**Heading:**" prefix and other markdown bold from
// clarifying-question text so it renders cleanly in the picker.
function stripBoldMarkdown(s: string): string {
  return s.replace(/\*\*(.+?)\*\*/g, '$1')
}

// Find the clarifying question that most likely covers an unknown.
// We score by shared word overlap (length >= 4, case-insensitive) and
// require at least 2 shared keywords to claim a match.
function findMatchingQuestion(unknown: string, questions: string[]): string | null {
  const tokens = (s: string) =>
    new Set(
      s
        .toLowerCase()
        .replace(/[^a-z0-9\s/_-]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length >= 4),
    )
  const uTok = tokens(unknown)
  let best: { q: string; score: number } | null = null
  for (const q of questions) {
    const qTok = tokens(q)
    let score = 0
    for (const w of uTok) if (qTok.has(w)) score += 1
    if (!best || score > best.score) best = { q, score }
  }
  return best && best.score >= 2 ? best.q : null
}

// Build 3–6 suggested answers for an unknown / clarifying question.
// We try the most specific patterns first and stop as soon as we have
// usable options:
//   1. Parenthesised CSV          — "(A, B, C)"  → A, B, C
//   2. Short inline alternation   — "X or Y"     → X, Y
//   3. Sentence-level alternation — "Whether <subj> must Y, or only Z"
//                                                → Y-predicate, only Z
//   4. Yes/No fallback for interrogatives.
//   5. Always append "Defer — answer in Phase 2".
function suggestAnswers(unknown: string, matchedQuestion: string | null): string[] {
  // Mine the UNKNOWN itself first — its own enumerations / alternations
  // are by definition the most relevant chip set. Only fall back to the
  // referenced clarifying question if the unknown has no internal
  // structure at all (otherwise unrelated questions can leak in chips).
  let found = mineOptions(unknown)
  if (found.length === 0 && matchedQuestion) {
    found = mineOptions(matchedQuestion)
  }

  // Yes/No fallback for interrogatives. We include "whether" so phrases
  // like "Whether X must Y, or only Z" still get a binary fallback if
  // the clause-alternation extractor came up empty.
  if (found.length === 0 && /\b(does|is|are|should|will|can|must|may|whether)\b/i.test(unknown)) {
    pushUnique(found, 'Yes')
    pushUnique(found, 'No')
  }

  // Always offer a "defer" escape hatch so the user can keep an item
  // open without inventing a fake answer.
  pushUnique(found, 'Defer — answer in Phase 2')

  // Cap suggestions to keep the UI compact.
  return found.slice(0, 6)
}

// Mine a single source string for option chips. Tries patterns in
// order of specificity:
//   1. Parenthesised CSV  — "(A, B, C)"     → A, B, C
//   2. Short alternation  — "A vs. B"       → A, B
//   3. Clause alternation — "Whether A, or B" (when 1 & 2 are empty)
function mineOptions(src: string): string[] {
  const out: string[] = []
  // Strip the trailing " — ask: <Owner>" annotation Phase-1 unknowns
  // carry, so we don't get "Yes" / "No" or random clause halves dirtied
  // by the owner phrase.
  const cleaned = src
    .replace(/\s*—\s*ask:.*$/i, '')
    .replace(/[?.]\s*$/, '')
    .trim()
  if (!cleaned) return out

  // 1) Parenthesised CSV: "(multi-tenant SaaS, single-tenant SaaS, on-prem)"
  //    Also splits on " / " so "(Low / Moderate / High)" and
  //    "(commercial vs government vs DoD)" get expanded into chips.
  //    The slash split is space-padded so we don't shred file paths
  //    or model names like "Claude/Opus".
  for (const m of cleaned.matchAll(/\(([^()]{4,200})\)/g)) {
    const inside = m[1]
    if (/^\s*e\.?g\.?\b/i.test(inside)) continue // skip "(e.g., Foo)" examples
    const parts = inside
      .split(/,\s*|\s+or\s+|\s+vs\.?\s+|\s+\/\s+/i)
      .map((p) => cleanCandidate(p))
      .filter((p) => p.length >= 2 && p.length <= 60)
    if (parts.length >= 2) for (const p of parts) pushUnique(out, p)
  }
  // 2) Short inline alternation: "X vs. Y" / "X or Y" with short tokens.
  for (const m of cleaned.matchAll(
    /\b([A-Za-z][A-Za-z0-9 _/-]{2,40})\s+(?:vs\.?|or)\s+([A-Za-z][A-Za-z0-9 _/-]{2,40})\b/g,
  )) {
    pushUnique(out, cleanCandidate(m[1]))
    pushUnique(out, cleanCandidate(m[2]))
  }
  // 3) Clause-level alternation only when nothing tighter matched.
  if (out.length === 0) {
    for (const opt of splitClauseAlternation(cleaned)) pushUnique(out, opt)
  }
  return out
}

// Clean a single candidate option. Strips Latin abbreviations (e.g.,
// i.e.), question-style prefixes ("which of", "what is/are", "how
// many"), and trailing predicate noise like "are guaranteed present per
// robot SKU" that gets pulled in when the parens contain a comma list
// followed by a verb phrase. We deliberately keep the cleaning
// conservative — short, recognisable nouns get through unmodified.
function cleanCandidate(raw: string): string {
  let s = raw.trim()
  s = s.replace(/^(?:e\.?g\.?,?\s*|i\.?e\.?,?\s*)/i, '')
  s = s.replace(/^(?:which\s+of|what\s+(?:is|are)|how\s+many|whether)\s+/i, '')
  s = s.replace(/\s+(?:are|is|will\s+be|may\s+be|must\s+be|can\s+be)\b.*$/i, '')
  return s.trim()
}

// Split a sentence on ", or " and return the two halves as candidate
// answer chips. Light cleaning: drop trailing "— ask: ..." annotations
// and the leading interrogative+subject+auxiliary on clause A so the
// chip shows the predicate verb-phrase ("queue telemetry locally for
// later sync") instead of the whole sentence prefix.
function splitClauseAlternation(src: string): string[] {
  const cleaned = src
    .replace(/\s*—\s*ask:.*$/i, '')
    .replace(/[?.]\s*$/, '')
    .trim()
  const m = cleaned.match(/^(.+?),\s+or\s+(.+)$/i)
  if (!m) return []
  let a = m[1].trim()
  const b = m[2].trim()
  // Optimistically strip an interrogative prefix so chip A reads as a
  // bare predicate. Bail and keep the original if the strip would empty
  // the clause out.
  const stripped = a.replace(
    /^(?:Whether|Does|Is|Are|Should|Will|May|Can|What|Which|How)\s+(?:[\w/&-]+(?:\s+[\w/&-]+){0,5}?\s+)?(?:must(?:\s+also)?|should(?:\s+also)?|will(?:\s+also)?|may(?:\s+also)?|can(?:\s+also)?|is|are|does|has|have)\s+/i,
    '',
  )
  if (stripped !== a && stripped.length >= 3) a = stripped
  const out: string[] = []
  if (a.length >= 3 && a.length <= 80) out.push(a)
  if (b.length >= 3 && b.length <= 80) out.push(b)
  return out
}

function pushUnique(arr: string[], val: string) {
  const v = val.trim()
  if (!v) return
  if (arr.some((x) => x.toLowerCase() === v.toLowerCase())) return
  arr.push(v)
}

// ---------------------------------------------------------------------- //
// Question-text cleaner for Deferred Unknown decisions.
//
// The backend gate emitter wraps the deferred-unknown text in a couple
// of decorations the architect doesn't need to see:
//
//   * `[Deferred Unknown]` (legacy / Q3 OFF)
//   * `[Deferred Unknown · <DecisionClass>]` (Q3 panel attached)
//   * `[Deferred Unknown · <DecisionClass> [architect review]]`
//   * `[Deferred Unknown · escalate to architect]` (router escalation)
//   * trailing ` — pick an option below.`
//   * trailing ` — ask: <Stakeholder>` (escalation path)
//
// Stripping all of these leaves the bare unknown text the LLM keyed
// `Suggested answers:` against, which is what `lookupSuggestions`
// needs as its key. Without this normalisation the lookup ALWAYS
// fails and the picker silently falls back to heuristic mining.
// ---------------------------------------------------------------------- //
function cleanDeferredUnknownQuestion(question: string): string {
  let q = question ?? ''
  // Drop the leading "[Deferred Unknown ...]" annotation entirely.
  q = q.replace(/^\s*\[Deferred Unknown(?:\s*·[^\]]*)?\]\s*/i, '')
  // Drop the trailing " — pick an option below" / " — pick an option below." hints.
  q = q.replace(/\s*[—-]\s*pick an option below\.?\s*$/i, '')
  // Drop the trailing " — ask: <stakeholder>" annotation.
  q = q.replace(/\s*[—-]\s*ask:\s*[^\n]*$/i, '')
  // Trailing period drop for parity with the Contextualist's keys
  // (charter strips the period, intent-map keeps it — normalise).
  q = q.replace(/\.\s*$/, '')
  return q.trim()
}

// ---------------------------------------------------------------------- //
// Virtual Architect panel option extractor.
//
// When the backend's Q3 Virtual Architect classifies a deferred unknown
// into a known decision class (e.g. "deployment_topology"), the gate
// emitter attaches the option-card panel as `decision.payload`. The
// payload shape is:
//
//   {
//     "decision_class": "deployment_topology",
//     "decision_class_title": "Deployment topology",
//     "options": [
//       { "id": "...", "title": "...", "summary": "...",
//         "pros": [...], "cons": [...], "recommended_when": "...",
//         "risk_level": "low|medium|high", "recommended": true|false }
//     ],
//     "confidence": 0.85,
//     "needs_architect_review": false,
//     "rationale": "...",
//     "source": "rule_layer"|"llm"|"fallback"
//   }
//
// Escalation payloads (`{"reason": "...", "needs_architect_review":
// true}` with no `options`) return an empty list so the caller falls
// back to the markdown chip set / heuristic mining.
// ---------------------------------------------------------------------- //
interface ArchitectPanelOption {
  id: string
  title: string
  summary: string
  recommended: boolean
}

function extractArchitectPanelOptions(
  payload: Record<string, unknown> | null | undefined,
): ArchitectPanelOption[] {
  if (!payload || typeof payload !== 'object') return []
  const options = (payload as { options?: unknown }).options
  if (!Array.isArray(options) || options.length === 0) return []
  const out: ArchitectPanelOption[] = []
  for (const raw of options) {
    if (!raw || typeof raw !== 'object') continue
    const obj = raw as Record<string, unknown>
    const id = typeof obj.id === 'string' ? obj.id : ''
    const title = typeof obj.title === 'string' ? obj.title : ''
    const summary = typeof obj.summary === 'string' ? obj.summary : ''
    if (!id || !title) continue
    out.push({
      id,
      title,
      summary,
      recommended: Boolean(obj.recommended),
    })
  }
  return out
}

// Serialise per-item answers into the single string the gate API
// expects. We use JSON because:
//   * Question text routinely contains the em-dash separator we used
//     before ("Whether ... — ask: …"), which corrupted round-trips.
//   * JSON lets us round-trip exactly without escaping every punctuation
//     character that might appear in either the question or the answer.
// The shape is `[ { "q": "<question>", "a": "<answer>" } ]`. Empty
// answers are dropped; an all-empty list serialises to "" (so the
// upstream gate.answer stays falsy and the decision counts as unanswered).
function serializeAnswers(items: string[], answers: string[]): string {
  const out: Array<{ q: string; a: string }> = []
  for (let i = 0; i < items.length; i++) {
    const a = (answers[i] ?? '').trim()
    if (a.length === 0) continue
    out.push({ q: items[i], a })
  }
  return out.length ? JSON.stringify(out) : ''
}

// Best-effort inverse: pull per-index answers out of a previously-
// serialised string so re-opening Edit shows the user's prior picks.
// Tries the new JSON format first, then falls back to the legacy
// "1. <q> — <a>" line format for backward compatibility.
function parseSerializedAnswers(serialized: string, count: number): string[] {
  const out = new Array<string>(count).fill('')
  if (!serialized) return out
  try {
    const parsed = JSON.parse(serialized)
    if (Array.isArray(parsed)) {
      for (let i = 0; i < parsed.length && i < count; i++) {
        const entry = parsed[i] as { a?: unknown } | null
        if (entry && typeof entry.a === 'string') out[i] = entry.a.trim()
      }
      return out
    }
  } catch {
    /* fall through to legacy parser */
  }
  // Legacy format: "1. <question> — <answer>". Question text often
  // contains its own em-dash (e.g. "Whether X — ask: Owner"), so a
  // greedy/lazy regex split picks the wrong dash. Walk to the LAST
  // " — " in each line and split there.
  for (const raw of serialized.split('\n')) {
    const idxMatch = raw.match(/^\s*(\d+)[.)]\s+(.*)$/)
    if (!idxMatch) continue
    const idx = parseInt(idxMatch[1], 10) - 1
    if (idx < 0 || idx >= count) continue
    const body = idxMatch[2]
    const lastDash = body.lastIndexOf(' — ')
    if (lastDash < 0) continue
    out[idx] = body.slice(lastDash + 3).trim()
  }
  return out
}

// ---------------------------------------------------------------------- //
// Test-only re-export of the pure parser helpers. Prefer this over
// adding extra `export` keywords above so the production bundle's
// public surface stays small.
// ---------------------------------------------------------------------- //
export const __testables = {
  extractClarifyingQuestions,
  extractDeferredUnknowns,
  extractSuggestionsByItem,
  lookupSuggestions,
  parseTopLevelItems,
  suggestAnswers,
  mineOptions,
  cleanDeferredUnknownQuestion,
  extractArchitectPanelOptions,
  applyPriorDecisions,
}


// ---------------------------------------------------------------------- //
// Cross-run carry-forward — Architect Decisions Downstream Flow Plan §8
// (manual-import variant). When the architect clicks "Import all" on
// the prior-decisions banner, we walk every current draft decision
// and overwrite the answer / status fields with the matching prior
// record's values. Records without a matching prior entry pass
// through unchanged. After import the architect can still edit any
// individual decision before submitting the gate.
// ---------------------------------------------------------------------- //
function applyPriorDecisions(
  current: GateDecisionRecord[],
  prior: PriorDecisionRecord[],
): GateDecisionRecord[] {
  const priorById = new Map(prior.map((p) => [p.id, p]))
  return current.map((d) => {
    const p = priorById.get(d.id)
    if (!p) return d
    return applyPriorToDecision(d, p)
  })
}

function applyPriorToDecision(
  d: GateDecisionRecord,
  p: PriorDecisionRecord,
): GateDecisionRecord {
  // Resolved prior — architect made a substantive choice. Carry it
  // forward as ``edited`` so the gate UI shows the chip + the
  // architect's answer.
  if (p.status === 'resolved' && p.decision) {
    return {
      ...d,
      status: 'edited' as GateDecisionStatus,
      answer: p.decision,
      skip_reason: null,
      rejection_reason: null,
    }
  }
  // Deferred prior — architect explicitly skipped at G1. Carry the
  // skip_reason across.
  if (p.status === 'deferred') {
    return {
      ...d,
      status: 'skipped' as GateDecisionStatus,
      answer: p.rationale ?? '',
      skip_reason: p.rationale,
      rejection_reason: null,
    }
  }
  // Not-applicable prior — architect rejected at G1 with rationale.
  if (p.status === 'not_applicable') {
    return {
      ...d,
      status: 'rejected' as GateDecisionStatus,
      answer: '',
      skip_reason: null,
      rejection_reason: p.rationale,
    }
  }
  // Open prior — nothing to import (the architect never answered it
  // in the prior run either). Leave the current draft untouched.
  return d
}



